import React, {useEffect, useMemo, useState} from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap";
import {useHistory} from "react-router";
import moment from "moment";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import {clickImg, telWithCommas} from "../../../utils";
import ploggingService from "./service/plogging.service";

const STORAGE_PREFIX = "shared-plogging-detail-";
const IMAGE_PREFIX = "https://d2twzv77g3c69u.cloudfront.net/fit-in/1000x700/";

const getPloggingValue = (item, key) => {
  if (!item) {
    return undefined;
  }
  if (item.plogging && item.plogging[key] !== undefined && item.plogging[key] !== null) {
    return item.plogging[key];
  }
  return item[key];
};

function SharedPloggingCalcMgmtPloggingDetail({match, location}) {
  const history = useHistory();
  const {ploggingId, centerName} = match.params;
  const storageKey = `${STORAGE_PREFIX}${ploggingId}`;

  const [item, setItem] = useState(() => {
    if (location?.state?.item) {
      return location.state.item;
    }
    const stored = sessionStorage.getItem(storageKey);
    if (!stored) {
      return null;
    }
    try {
      return JSON.parse(stored);
    } catch (e) {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const formatActivityTime = (value) => {
    if (!value) return "-";
    const raw = String(value).trim();
    const time = raw.includes(" ") ? raw.split(" ").pop() : raw;
    return time.split(":").slice(0, 2).join(":");
  };

  const formatAddress = (value) => {
    if (!value) return "-";
    const parts = String(value).trim().split(/\s+/);
    return parts.length > 2 ? parts.slice(2).join(" ") : value;
  };

  useEffect(() => {
    if (location?.state?.item) {
      sessionStorage.setItem(storageKey, JSON.stringify(location.state.item));
      setItem(location.state.item);
    }
  }, [location, storageKey]);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const res = await ploggingService.adminDetail(ploggingId);
        if (res.code === 200 && res.data) {
          setItem(res.data);
          sessionStorage.setItem(storageKey, JSON.stringify(res.data));
        }
      } catch (e) {
        // Keep fallback state/sessionStorage if direct fetch fails.
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [ploggingId, storageKey]);

  const routePoints = useMemo(() => {
    const routeData = item?.data || item?.plogging?.data;
    if (!routeData) {
      return [];
    }
    try {
      const raw = typeof routeData === "string" ? JSON.parse(routeData) : routeData;
      return Array.isArray(raw) ? raw.filter(v => v?.latitude && v?.longitude) : [];
    } catch (e) {
      return [];
    }
  }, [item]);

  useEffect(() => {
    if (!item || !routePoints.length || !window.kakao?.maps) {
      return;
    }

    const {kakao} = window;
    const mapContainer = document.getElementById("shared-plogging-route-map");
    if (!mapContainer) {
      return;
    }

    const firstPoint = routePoints[0];
    const map = new kakao.maps.Map(mapContainer, {
      center: new kakao.maps.LatLng(Number(firstPoint.latitude), Number(firstPoint.longitude)),
      level: 6,
    });

    const linePath = routePoints.map(
      (point) => new kakao.maps.LatLng(Number(point.latitude), Number(point.longitude))
    );

    const polyline = new kakao.maps.Polyline({
      path: linePath,
      strokeWeight: 5,
      strokeColor: "#2EACFF",
      strokeOpacity: 0.9,
      strokeStyle: "solid",
    });
    polyline.setMap(map);

    const bounds = new kakao.maps.LatLngBounds();
    linePath.forEach((point) => bounds.extend(point));
    map.setBounds(bounds);

    new kakao.maps.Marker({
      map,
      position: linePath[0],
      title: "시작",
    });

    new kakao.maps.Marker({
      map,
      position: linePath[linePath.length - 1],
      title: "종료",
    });
  }, [item, routePoints]);

  const remove = async () => {
    if (!item?.id) {
      return;
    }
    if (!window.confirm("삭제하시겠습니까?")) {
      return;
    }
    await ploggingService.remove(Number(item.id));
    alert("삭제되었습니다.");
    sessionStorage.removeItem(storageKey);
    history.goBack();
  };

  if (loading && !item) {
    return null;
  }

  if (!item) {
    return (
      <>
        <SimpleHeader name="Tables" parentName="Tables"/>
        <Container className="mt--6" fluid>
          <Card>
            <CardBody className="py-5 text-center">
              표시할 활동 내역이 없습니다.
            </CardBody>
          </Card>
        </Container>
      </>
    );
  }

  const activityImages = (item.plogging?.ploggingImages || []).filter(v => v.diff === "half");
  const trashImages = (item.plogging?.ploggingImages || []).filter(v => v.diff === "trash");

  return (
    <>
      <SimpleHeader name="Tables" parentName="Tables"/>
      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card>
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">{centerName} 활동 상세</h3>
                  </Col>
                  <Col xs="4" className="text-right">
                    <Button color="secondary" size="sm" onClick={() => history.goBack()}>
                      목록
                    </Button>
                    <Button color="danger" size="sm" className="ml-2" onClick={remove}>
                      삭제
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md="6">
                    <h4 className="mb-3">기본 정보</h4>
                    <Table responsive striped>
                      <tbody>
                      <tr>
                        <th className="text-center">이름</th>
                        <td className="text-center">{item.user?.name || "-"}</td>
                        <th className="text-center">연락처</th>
                        <td className="text-center">{telWithCommas(item.user?.tel) || "-"}</td>
                      </tr>
                      <tr>
                        <th className="text-center">등록일</th>
                        <td className="text-center">{moment(getPloggingValue(item, "createdAt")).format("YYYY-MM-DD")}</td>
                        <th className="text-center">봉사시간</th>
                        <td className="text-center">{getPloggingValue(item, "time") || "-"}</td>
                      </tr>
                      <tr>
                        <th className="text-center">활동시간</th>
                        <td className="text-center" colSpan="3">
                          {formatActivityTime(getPloggingValue(item, "startTime"))} ~ {formatActivityTime(getPloggingValue(item, "endTime"))}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-center">GPS주소</th>
                        <td className="text-center" colSpan="3">{formatAddress(getPloggingValue(item, "address"))}</td>
                      </tr>
                      <tr>
                        <th className="text-center">VMS번호</th>
                        <td className="text-center">{item.user?.vmsNumber || "-"}</td>
                        <th className="text-center">1365번호</th>
                        <td className="text-center">{item.user?.number1365 || "-"}</td>
                      </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md="6">
                    <h4 className="mb-3">탄소발자국 요약</h4>
                    <Row>
                      <Col md="4" className="mb-3">
                        <Card body className="text-center mb-0">
                          <div className="text-muted small">활동거리</div>
                          <div className="h3 mb-0">{getPloggingValue(item, "stepCo2")?.toFixed?.(2) || Number(getPloggingValue(item, "stepCo2") || 0).toFixed(2)}kg</div>
                        </Card>
                      </Col>
                      <Col md="4" className="mb-3">
                        <Card body className="text-center mb-0">
                          <div className="text-muted small">쓰레기 제자원화</div>
                          <div className="h3 mb-0">{getPloggingValue(item, "co2")?.toFixed?.(2) || Number(getPloggingValue(item, "co2") || 0).toFixed(2)}kg</div>
                        </Card>
                      </Col>
                      <Col md="4" className="mb-3">
                        <Card body className="text-center mb-0">
                          <div className="text-muted small">총 탄소발자국</div>
                          <div className="h3 mb-0">{getPloggingValue(item, "totalCo2")?.toFixed?.(2) || Number(getPloggingValue(item, "totalCo2") || 0).toFixed(2)}kg</div>
                        </Card>
                      </Col>
                    </Row>
                    <div className="mt-2">
                      <Badge color={getPloggingValue(item, "check") ? "success" : "secondary"} pill>
                        {getPloggingValue(item, "check") ? "체크 완료" : "미체크"}
                      </Badge>
                    </div>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col>
                    <h4 className="mb-3">활동 경로</h4>
                    <div
                      id="shared-plogging-route-map"
                      style={{
                        width: "100%",
                        height: "420px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        background: "#f6f9fc",
                      }}
                    />
                    {!routePoints.length && (
                      <div className="text-muted mt-3">저장된 활동 경로가 없습니다.</div>
                    )}
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col>
                    <h4 className="mb-3">활동 사진</h4>
                    <Row>
                      {activityImages.length ? activityImages.map((image) => (
                        <Col md="3" key={image.id || image.name} className="mb-4">
                          <img
                            alt=""
                            src={`${IMAGE_PREFIX}${image.name}`}
                            onClick={() => clickImg(`${IMAGE_PREFIX}${image.name}`)}
                            style={{width: "100%", height: 200, objectFit: "cover", borderRadius: 8, cursor: "pointer"}}
                          />
                        </Col>
                      )) : (
                        <Col><div className="text-muted">활동 사진이 없습니다.</div></Col>
                      )}
                    </Row>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col>
                    <h4 className="mb-3">쓰레기 사진</h4>
                    <Row>
                      {trashImages.length ? trashImages.map((image) => (
                        <Col md="3" key={image.id || image.name} className="mb-4">
                          <img
                            alt=""
                            src={`${IMAGE_PREFIX}${image.name}`}
                            onClick={() => clickImg(`${IMAGE_PREFIX}${image.name}`)}
                            style={{width: "100%", height: 200, objectFit: "cover", borderRadius: 8, cursor: "pointer"}}
                          />
                        </Col>
                      )) : (
                        <Col><div className="text-muted">쓰레기 사진이 없습니다.</div></Col>
                      )}
                    </Row>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col>
                    <h4 className="mb-3">활동 내역</h4>
                    <Table responsive striped>
                      <thead className="thead-light">
                      <tr>
                        <th className="text-center">품목</th>
                        <th className="text-center">수량</th>
                        <th className="text-center">탄소저감량</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr><td className="text-center">종이류</td><td className="text-center">{getPloggingValue(item, "paper") || 0}</td><td className="text-center">-</td></tr>
                      <tr><td className="text-center">유리류</td><td className="text-center">{getPloggingValue(item, "glass") || 0}</td><td className="text-center">{getPloggingValue(item, "glass") ? (getPloggingValue(item, "glass") * 0.421).toFixed(2) : 0}KG</td></tr>
                      <tr><td className="text-center">캔류</td><td className="text-center">{getPloggingValue(item, "can") || 0}</td><td className="text-center">{getPloggingValue(item, "can") ? (getPloggingValue(item, "can") * 0.3).toFixed(2) : 0}KG</td></tr>
                      <tr><td className="text-center">매입/소각류</td><td className="text-center">{getPloggingValue(item, "landfill") || 0}</td><td className="text-center">-</td></tr>
                      <tr><td className="text-center">스트로폼류</td><td className="text-center">{getPloggingValue(item, "styrofoam") || 0}</td><td className="text-center">-</td></tr>
                      <tr><td className="text-center">페트류</td><td className="text-center">{getPloggingValue(item, "pet") || 0}</td><td className="text-center">{getPloggingValue(item, "pet") ? (getPloggingValue(item, "pet") * 0.011).toFixed(2) : 0}KG</td></tr>
                      <tr><td className="text-center">비닐류</td><td className="text-center">{getPloggingValue(item, "vinyl") || 0}</td><td className="text-center">-</td></tr>
                      <tr><td className="text-center">플라스틱류</td><td className="text-center">{getPloggingValue(item, "plastic") || 0}</td><td className="text-center">{getPloggingValue(item, "plastic") ? (getPloggingValue(item, "plastic") * 0.023).toFixed(2) : 0}KG</td></tr>
                      <tr><td className="text-center">담배꽁초</td><td className="text-center">{getPloggingValue(item, "cigarette") || 0}</td><td className="text-center">-</td></tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default SharedPloggingCalcMgmtPloggingDetail;
